<template>
    <Spinner v-if="isLoading" />
    <template v-else>
        <InfoMessage
            title="Set your strategic direction"
            storageKey="welcome-message-strategy-vision"
            :forceShow="!doc?.content"
            showToggle
        >
            <p>
                Vision & mission is your organization's living strategy
                document: the future you are building toward and the bet you are
                making to get there.
            </p>
        </InfoMessage>

        <Page>
            <template #eyebrow>
                <Eyebrow icon="pi pi-compass">Vision & mission</Eyebrow>
            </template>
            <LastChangedInfo
                v-if="stek?.identity"
                class="last-changed"
                :entityId="stek.identity"
                :entityType="EntityType.STEK"
                attribute="mission_vision"
            />
            <Section class="vision">
                <div
                    v-if="!doc?.content && !isEditingStrategy"
                    class="vision__empty"
                >
                    <i class="pi pi-compass vision__empty-icon" />
                    <h3>No vision & mission yet</h3>
                    <p v-if="canEditStrategy">
                        <i>Switch to Edit to set your stek's direction.</i>
                    </p>
                </div>
                <template v-else-if="doc">
                    <TextAppArtefact
                        class="vision__doc"
                        v-model="doc"
                        :isEditing="isEditingStrategy"
                    />
                </template>
            </Section>
        </Page>
    </template>
</template>

<script setup lang="ts">
import { throttle } from "lodash";
import { useToast } from "primevue/usetoast";
import { computed, onUnmounted, ref, watch } from "vue";

import Eyebrow from "@/components/Eyebrow.vue";
import InfoMessage from "@/components/InfoMessage.vue";
import LastChangedInfo from "@/components/LastChangedInfo.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import TextAppArtefact from "@/components/TextAppArtefact.vue";
import { FILE_SYNC_THROTTLE_MS } from "@/composables/draft-manager";
import { useEditManager } from "@/composables/edit-manager";
import { registerFlush, unregisterFlush } from "@/composables/pending-flush";
import { useStekManager } from "@/composables/stek-manager";
import type { Artefact } from "@/lib/artefacts";
import { getMissionVisionArtefact } from "@/lib/artefacts/markdown";
import { useServices } from "@/lib/openapi";
import Spinner from "@/shared/Spinner.vue";
import { EntityType } from "@/stores/entity-store";
import { ClusterRole, useUserManager } from "@/stores/user-store";

const toast = useToast();
const { stek } = useStekManager();
const { isEditing } = useEditManager();
const { currentOrganizationRoles } = useUserManager();
const api = useServices({
    opts: { throwOnError: false },
    requires: { auth: true, organization: true, stek: true },
});

const doc = ref<Artefact<"text/markdown">>();
const isLoadingDoc = ref(false);

// Loading until the stek resolves (it does so async, after the steks list) or its document fetch is in flight.
const isLoading = computed(() => !stek.value || isLoadingDoc.value);

// Editing the strategy document is restricted to stek managers (the backend
// enforces this too); keep the editor read-only for everyone else.
const canEditStrategy = computed(() =>
    currentOrganizationRoles.value.includes(ClusterRole.STEK_MANAGER)
);
const isEditingStrategy = computed(
    () => isEditing.value && canEditStrategy.value
);

// Load each stek's mission & vision document once, and don't reload over the
// same stek's unsaved edits.
let loadedIdentity: string | undefined;
watch(
    stek,
    async (current) => {
        if (!current || current.identity === loadedIdentity) return;
        loadedIdentity = current.identity;
        isLoadingDoc.value = true;
        try {
            doc.value = await getMissionVisionArtefact(
                current,
                "mission_vision"
            );
        } finally {
            isLoadingDoc.value = false;
        }
    },
    { immediate: true }
);

// Autosave on edit→view and while typing (throttled), matching how the stek
// and other entity pages persist their artefacts — no explicit save button.
const saveStrategy = async () => {
    if (!stek.value || !canEditStrategy.value || !doc.value?.isModified) return;
    const sent = doc.value.content;
    try {
        const { response } = await api.startStekStekMgtUpdateStrategyStartPost({
            body: {
                stek_id: stek.value.identity,
                mission_vision: doc.value.blob,
            },
        });
        if (response.ok) {
            // Only reset the baseline if nothing changed while the request was
            // in flight; otherwise keep the delta dirty for the next save.
            if (doc.value && doc.value.content === sent) {
                await doc.value.persist();
            }
        } else if (response.status === 403) {
            toast.add({
                severity: "warn",
                summary: "Not allowed",
                detail: "Only stek managers can edit the strategy document.",
                life: 5000,
            });
        } else {
            throw new Error(String(response.status));
        }
    } catch {
        toast.add({
            severity: "error",
            summary: "Save failed",
            detail: "Please try again.",
            life: 5000,
        });
    }
};

const docIsModified = computed(() => doc.value?.isModified ?? false);
const throttledSave = throttle(saveStrategy, FILE_SYNC_THROTTLE_MS);

watch([isEditing, docIsModified], ([_editing, modified]) => {
    if (modified) {
        throttledSave();
    }
});

registerFlush(saveStrategy);
onUnmounted(() => {
    throttledSave.cancel();
    unregisterFlush(saveStrategy);
});
</script>

<style lang="scss" scoped>
.vision {
    padding-top: 0;
}

.last-changed {
    position: absolute;
    top: 0;
    right: 0;
}

.vision__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    text-align: center;
    padding: 3rem 0;
    color: var(--text-color-secondary);

    > * {
        margin: 0;
    }
    .vision__empty-icon {
        font-size: 3rem;
    }
}
</style>
